import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import { formatTRY, formatDate, PARTY_TYPE_LABELS, PARTY_TYPE_COLORS, CATEGORY_LABELS } from "@/components/finance/financeLabels";
import { ArrowLeft, Mail, Phone, MapPin, Building2 } from "lucide-react";

export default function PartyDetailPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/admin/finance/parties/${id}/statement?limit=500`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [id]);

    if (loading || !data) return <FinanceShell><div className="py-12 text-center text-slate-500">Yükleniyor...</div></FinanceShell>;
    const p = data.party;

    return (
        <FinanceShell>
            <Link to="/admin/finance/parties" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft className="h-4 w-4" /> Cariler</Link>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-1.5">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${PARTY_TYPE_COLORS[p.type] || ""}`}>{PARTY_TYPE_LABELS[p.type]}</span>
                            <span className="font-mono text-xs text-slate-400">{p.code}</span>
                        </div>
                        <h2 className="text-2xl font-bold">{p.name}</h2>
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                            {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</span>}
                            {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</span>}
                            {p.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.address}</span>}
                            {p.tax_id && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> VKN: {p.tax_id}</span>}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Bakiye</div>
                        <div className={`text-3xl font-extrabold ${data.balance.balance > 0.01 ? "text-emerald-600" : data.balance.balance < -0.01 ? "text-rose-600" : "text-slate-700"}`} data-testid="party-balance">
                            {formatTRY(data.balance.balance)}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                            Borç: {formatTRY(data.balance.debit)} · Alacak: {formatTRY(data.balance.credit)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
                    <h3 className="font-bold">Cari Ekstre ({data.total} hareket)</h3>
                </div>
                {data.items.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">Henüz hareket yok.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="statement-table">
                            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Tarih</th>
                                    <th className="px-3 py-2 text-left">Fiş No</th>
                                    <th className="px-3 py-2 text-left">Açıklama</th>
                                    <th className="px-3 py-2 text-left">Kategori</th>
                                    <th className="px-3 py-2 text-right">Borç</th>
                                    <th className="px-3 py-2 text-right">Alacak</th>
                                    <th className="px-3 py-2 text-right">Bakiye</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((it) => (
                                    <tr key={it.id} className={`border-t border-slate-100 dark:border-slate-800/50 ${it.is_void ? "opacity-40 line-through" : ""}`}>
                                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(it.date)}</td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{it.entry_no}</td>
                                        <td className="px-3 py-2 max-w-[300px] truncate" title={it.description}>{it.description || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{CATEGORY_LABELS[it.category] || it.category}</td>
                                        <td className="px-3 py-2 text-right text-rose-600 dark:text-rose-400">{it.direction === "debit" ? formatTRY(it.amount_try) : "—"}</td>
                                        <td className="px-3 py-2 text-right text-emerald-600 dark:text-emerald-400">{it.direction === "credit" ? formatTRY(it.amount_try) : "—"}</td>
                                        <td className="px-3 py-2 text-right font-bold">{formatTRY(it.running_balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </FinanceShell>
    );
}
